/**
 * AutoSaveIndicator - Indicador visual del estado de auto-guardado
 *
 * Muestra al usuario:
 * - Guardando... (cuando está en proceso)
 * - Guardado hace X minutos (cuando se guardó)
 * - Cambios sin guardar (cuando hay cambios pendientes)
 */

import React from 'react';
import { Cloud, CloudOff, Loader2, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { AutoSaveState } from '../../../hooks/useVaultAutoSave';

interface AutoSaveIndicatorProps {
  state: AutoSaveState;
  className?: string;
}

/**
 * Formatea el tiempo relativo (hace X minutos, etc.)
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 10) {
    return 'ahora';
  }
  if (diffSeconds < 60) {
    return `hace ${diffSeconds}s`;
  }
  if (diffMinutes < 60) {
    return `hace ${diffMinutes}m`;
  }
  if (diffHours < 24) {
    return `hace ${diffHours}h`;
  }
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
  });
}

export const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
  state,
  className,
}) => {
  const { isSaving, lastSaved, hasPendingChanges, hasStoredData } = state;

  // Estado: Guardando
  if (isSaving) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs text-gray-500',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Guardando...</span>
      </div>
    );
  }

  // Estado: Cambios pendientes (esperando debounce)
  if (hasPendingChanges) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs text-amber-600',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <CloudOff className="h-3 w-3" />
        <span>Sin guardar</span>
      </div>
    );
  }

  // Estado: Guardado exitosamente
  if (lastSaved) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs text-green-600',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <Check className="h-3 w-3" />
        <span>Guardado {formatRelativeTime(lastSaved)}</span>
      </div>
    );
  }

  // Estado: Tiene datos guardados (de sesión anterior)
  if (hasStoredData) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs text-blue-600',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <Cloud className="h-3 w-3" />
        <span>Borrador recuperado</span>
      </div>
    );
  }

  // Estado: Sin datos guardados
  return null;
};

export default AutoSaveIndicator;
